import React, { useState, useEffect, useRef } from 'react';
import './Competences.css';

const Competences = ({ isVisible, onClose }) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isLoaded, setIsLoaded] = useState(false);
  const [activeCategory, setActiveCategory] = useState('langages');
  const [animatedItems, setAnimatedItems] = useState([]);
  
  const containerRef = useRef(null);
  const particlesRef = useRef(null);
  const carouselRef = useRef(null);
  
  // Variables pour les particules
  const numParticles = 60;
  const particles = useRef([]);
  
  // Liste complète des compétences avec leurs logos et niveaux
  const skillsData = {
    langages: [
      { name: 'HTML5', logo: '/assets/images/langue-logo/html.png', level: 95 },
      { name: 'CSS3', logo: '/assets/images/langue-logo/css.png', level: 90 },
      { name: 'JavaScript', logo: '/assets/images/langue-logo/js.png', level: 85 },
      { name: 'PHP', logo: '/assets/images/langue-logo/php.png', level: 80 },
      { name: 'SQL', logo: '/assets/images/langue-logo/sql.png', level: 75 },
      { name: 'Java', logo: '/assets/images/langue-logo/java.png', level: 20 }
    ],
    frameworks: [
      { name: 'React', logo: '/assets/images/langue-logo/react.png', level: 80 },
      { name: 'Bootstrap', logo: '/assets/images/langue-logo/Bootstrap.png', level: 85 },
      { name: 'Symfony', logo: '/assets/images/langue-logo/symfony.png', level: 70 },
      { name: 'SASS/SCSS', logo: '/assets/images/langue-logo/sass.png', level: 75 },
      { name: 'Express', logo: '/assets/images/langue-logo/express.webp', level: 65 },
      { name: 'jQuery', logo: '/assets/images/langue-logo/jquery.png', level: 75 }
    ],
    outils: [
      { name: 'Git/GitHub', logo: '/assets/images/langue-logo/github-logo.png', level: 85 },
      { name: 'Docker', logo: '/assets/images/langue-logo/docker.png', level: 70 },
      { name: 'VirtualBox', logo: '/assets/images/langue-logo/virtualBox.png', level: 65 },
      { name: 'Gimp', logo: '/assets/images/langue-logo/gimp-logo.png', level: 60 },
      { name: 'WordPress', logo: '/assets/images/langue-logo/wordpress.png', level: 80 },
    ],
    autres: [
      { name: 'Responsive Design', logo: '/assets/images/langue-logo/responsive.png', level: 90 },
      { name: 'RESTful API', logo: '/assets/images/langue-logo/api.png', level: 75 },
      { name: 'Nodejs', logo: '/assets/images/langue-logo/nodejs.png', level: 75 },
      { name: 'NoSQL', logo: '/assets/images/langue-logo/nosql.png', level: 65 },
      { name: 'SEO', logo: '/assets/images/langue-logo/seo.png', level: 70 },
      { name: 'Agile/Scrum', logo: '/assets/images/langue-logo/agile.png', level: 75 }
    ]
  };
  
  // Catégories de compétences
  const categories = [
    { id: 'langages', name: 'Langages', icon: 'fas fa-code' },
    { id: 'frameworks', name: 'Frameworks', icon: 'fas fa-layer-group' },
    { id: 'outils', name: 'Outils', icon: 'fas fa-tools' },
    { id: 'autres', name: 'Autres', icon: 'fas fa-plus-circle' }
  ];
  
  // Initialisation des particules
  useEffect(() => {
    if (isVisible && particlesRef.current) {
      for (let i = 0; i < numParticles; i++) {
        particles.current.push({
          x: Math.random() * window.innerWidth,
          y: Math.random() * window.innerHeight,
          size: Math.random() * 3 + 1,
          speedX: Math.random() * 2 - 1,
          speedY: Math.random() * 2 - 1,
          opacity: Math.random() * 0.5 + 0.1,
        });
      }
      
      const canvas = particlesRef.current;
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      
      animateParticles();
    }
    
    // Nettoyage
    return () => {
      particles.current = [];
    };
  }, [isVisible]);
  
  // Animation des particules
  const animateParticles = () => {
    if (!particlesRef.current || !isVisible) return;
    
    const canvas = particlesRef.current;
    const ctx = canvas.getContext('2d');
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    particles.current.forEach(particle => {
      // Mouvement
      particle.x += particle.speedX;
      particle.y += particle.speedY;
      
      // Rebond sur les bords
      if (particle.x < 0 || particle.x > canvas.width) particle.speedX *= -1;
      if (particle.y < 0 || particle.y > canvas.height) particle.speedY *= -1;
      
      // Dessin de la particule
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(179, 135, 246, ${particle.opacity})`;
      ctx.fill();
      
      // Connexion entre particules proches
      particles.current.forEach(otherParticle => {
        const dx = particle.x - otherParticle.x;
        const dy = particle.y - otherParticle.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < 100) {
          ctx.beginPath();
          ctx.moveTo(particle.x, particle.y);
          ctx.lineTo(otherParticle.x, otherParticle.y);
          ctx.strokeStyle = `rgba(179, 135, 246, ${0.2 - distance/500})`;
          ctx.lineWidth = 0.5;
          ctx.stroke();
        }
      });
    });
    
    requestAnimationFrame(animateParticles);
  };
  
  // Effet de suivi du curseur
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!containerRef.current) return;
      
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      setMousePosition({ x, y });
    };
    
    if (isVisible) {
      window.addEventListener('mousemove', handleMouseMove);
      // Animation d'entrée
      setTimeout(() => setIsLoaded(true), 300);
    } else {
      setIsLoaded(false);
    }
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isVisible]);
  
  // Animation du carousel
  useEffect(() => {
    if (isVisible && carouselRef.current) {
      const carousel = carouselRef.current;
      const totalItems = Object.values(skillsData).flat().length;
      const radius = 400; // Rayon pour espacer les éléments
      let angle = 0;
      let requestId;
      
      // Récupérer tous les éléments du carousel
      const items = carousel.querySelectorAll('.competences-carousel-item');
      
      // Fonction pour positionner les éléments avec un espacement uniforme
      const positionItems = (currentAngle) => {
        items.forEach((item, index) => {
          // Distribution angulaire uniforme sur 360 degrés
          // Utilisez (360 / totalItems) pour un espacement égal entre tous les éléments
          const itemAngle = currentAngle + (index * (360 / totalItems));
          const itemRadian = (itemAngle * Math.PI) / 180;
          
          const x = radius * Math.sin(itemRadian);
          const z = radius * Math.cos(itemRadian);
          
          // Application de la transformation
          item.style.transform = `translate3d(${x}px, 0px, ${z}px) scale(0.85)`;
          
          // Ajuster l'opacité en fonction de la position Z
          const zPosition = Math.cos(itemRadian);
          // Amélioration de l'opacité pour une meilleure visibilité
          const opacity = 0.5 + (0.5 * (zPosition + 1) / 2); // Valeur entre 0.5 et 1
          item.style.opacity = opacity;
        });
      };
      
      // Position initiale
      positionItems(angle);
      
      const rotateCarousel = () => {
        angle += 0.2; // Vitesse de rotation
        positionItems(angle);
        requestId = requestAnimationFrame(rotateCarousel);
      };
      
      requestId = requestAnimationFrame(rotateCarousel);
      
      return () => {
        cancelAnimationFrame(requestId);
      };
    }
  }, [isVisible, skillsData]);
  
  // Animation des items au changement de catégorie
  useEffect(() => {
    setAnimatedItems([]);
    
    if (isVisible) {
      const skills = skillsData[activeCategory] || [];
      const timer = setTimeout(() => {
        skills.forEach((_, index) => {
          setTimeout(() => {
            setAnimatedItems(prev => [...prev, index]);
          }, index * 100);
        });
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [activeCategory, isVisible]);
  
  // Définition de la position du gradient qui suit la souris
  const gradientPosition = `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(179, 135, 246, 0.2) 0%, rgba(92, 122, 250, 0.1) 40%, transparent 70%)`;

  return (
    <div 
      className={`competences-section ${isVisible ? 'visible' : ''}`}
      aria-hidden={!isVisible}
      ref={containerRef}
    >
      {/* Effet de particules en arrière-plan */}
      <canvas 
        ref={particlesRef} 
        className="particles-canvas"
      />
      
      {/* Gradient qui suit la souris */}
      <div 
        className="mouse-gradient"
        style={{ background: gradientPosition }}
      />
      
      {/* Bouton de fermeture */}
      <div 
        className="competences-close" 
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onClose();
          }
        }}
        role="button"
        aria-label="Fermer la section Compétences"
        tabIndex={isVisible ? 0 : -1}
      >
        <span></span>
        <span></span>
      </div>
      
      <div className={`competences-container ${isLoaded ? 'loaded' : ''}`}>
        <div className="competences-header">
          <h2 className="competences-title">Compétences</h2>
          <p className="competences-subtitle">
            <span>Mes compétences en développement web : langages et technologies maîtrisés</span>
          </p>
        </div>
        
        <div className="competences-content">
          {/* Navigation par catégorie */}
          <div className="competences-tabs">
            {categories.map((category) => (
              <button
                key={category.id}
                className={`category-tab ${activeCategory === category.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(category.id)}
              >
                <i className={category.icon}></i>
                <span>{category.name}</span>
              </button>
            ))}
          </div>
          
          {/* Affichage des compétences */}
          <div className="skills-grid">
            {skillsData[activeCategory]?.map((skill, index) => (
              <div 
                key={skill.name} 
                className={`skill-card ${animatedItems.includes(index) ? 'animated' : ''}`}
              >
                <div className="skill-icon">
                  <img src={skill.logo} alt={skill.name} loading="lazy"/>
                </div>
                <h3 className="skill-name">{skill.name}</h3>
                <div className="skill-bar-container">
                  <div className="skill-level">{skill.level}%</div>
                  <div className="skill-bar" style={{ width: `${skill.level}%` }}>
                    <div 
                      className="skill-progress" 
                      style={{ width: `${skill.level}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Carousel 3D */}
        <div className="carousel-wrapper">
    <h3 className="carousel-title">Technologies maîtrisées</h3>
    <div className="carousel-container">
      <div className="carousel-content" ref={carouselRef}>
        {Object.values(skillsData).flat().map((skill, index) => (
          <div 
            key={`carousel-${skill.name}`} 
            className="competences-carousel-item"
          >
            <img 
              src={skill.logo} 
              alt={skill.name}
              className="carousel-image"
              loading="lazy"
            />
          </div>
        ))}
      </div>
    </div>
  </div>
        
        {/* Statistiques */}
        {/* <div className="competences-stats">
          <div className="stat-card">
            <div className="stat-icon"><i className="fas fa-code"></i></div>
            <div className="stat-value">{Object.values(skillsData).flat().length}</div>
            <div className="stat-label">Technologies</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon"><i className="fas fa-project-diagram"></i></div>
            <div className="stat-value">30+</div>
            <div className="stat-label">Projets réalisés</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon"><i className="fas fa-award"></i></div>
            <div className="stat-value">4</div>
            <div className="stat-label">Certifications</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon"><i className="fas fa-laptop-code"></i></div>
            <div className="stat-value">2+</div>
            <div className="stat-label">Années d'expérience</div>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default Competences;